/**
 * SourceStep — where the new People List comes from (epic
 * 2026-10-15-thrive-search-import, sub-task 03).
 *
 * Two cards: "Pull from a Thrive search" (the default, tagged so) and "Upload
 * a file" (the upload wizard). Rendered where `thrive-search-import` is
 * visible (released 2026-10-20); with the flag off the page shows the upload
 * wizard with no Source step at all.
 *
 * Tokenized: token color utilities + `text-*` roles. No inline styles.
 */

export type ImportSource = "thrive" | "upload";

export interface SourceStepProps {
  value: ImportSource;
  onChange: (source: ImportSource) => void;
}

const CARDS: ReadonlyArray<{
  source: ImportSource;
  title: string;
  description: string;
  isDefault: boolean;
}> = [
  {
    source: "thrive",
    title: "Pull from a Thrive search",
    description: "Bring a search's candidacies in as a People List, no file needed.",
    isDefault: true,
  },
  {
    source: "upload",
    title: "Upload a file",
    description: "A ChopLink CSV or JSON export.",
    isDefault: false,
  },
];

export function SourceStep({ value, onChange }: SourceStepProps) {
  return (
    <fieldset data-testid="source-step" className="flex flex-col gap-3">
      <legend className="mb-3 text-label text-text">Source</legend>
      <div className="grid gap-3 sm:grid-cols-2">
        {CARDS.map((card) => {
          const pressed = value === card.source;
          return (
            <button
              key={card.source}
              type="button"
              data-testid={`source-card-${card.source}`}
              aria-pressed={pressed}
              onClick={() => onChange(card.source)}
              className={`flex flex-col gap-1 rounded-md border px-4 py-3 text-left transition-colors ${
                pressed
                  ? "border-accent bg-accent-muted"
                  : "border-border bg-surface hover:border-border-hover"
              }`}
            >
              <span className="flex items-center gap-2 text-body text-text">
                {card.title}
                {card.isDefault ? (
                  <span
                    data-testid="source-default-tag"
                    className="rounded-full bg-surface-raised px-2 py-0.5 text-label text-text-secondary"
                  >
                    Default
                  </span>
                ) : null}
              </span>
              <span className="text-caption text-text-secondary">{card.description}</span>
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
